import type { AdjustmentType, MatchedObjectType } from '@eriko/shared-types';
import { formatCurrency, formatDate } from '@eriko/shared-types';
import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Input } from '@eriko/ui/src/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eriko/ui/src/components/ui/select';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  useMatchTargets,
  useProposeMatch,
} from '../../hooks/useReconciliation';

function useDebounce<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export interface MatchTarget {
  transactionId: string;
  bankTransaction: {
    description: string;
    amount: string;
    date: string;
    type?: 'DEBIT' | 'CREDIT';
  };
  /** ISO 4217 code for the bank-line currency. */
  currency?: string;
}

/** Payload reported back so the chat card can collapse the row into a
 *  Wonso-style confirmation strip in place after a successful propose. */
export interface MatchProposeResolution {
  matchedSummary: string;
  matchedAmount: string | null;
  variance: number | null;
}

interface Props {
  target: MatchTarget;
  onClose: () => void;
  /** Called after a successful propose-match. The chat card uses this to
   *  replace the row's action buttons with an inline confirmation strip. */
  onProposed?: (payload: MatchProposeResolution) => void;
}

const ADJUSTMENT_TYPE_LABELS: Record<AdjustmentType, string> = {
  PROCESSING_FEE: 'Processing fee',
  BANK_FEE: 'Bank fee',
  FX_DIFFERENCE: 'FX difference',
  DISCOUNT: 'Discount',
  WRITE_OFF: 'Write-off',
  INTEREST_EARNED: 'Interest earned',
  ROUNDING: 'Rounding',
  OTHER: 'Other',
};

/**
 * SME-side "Match to existing" dialog. Lets the SME (or reviewer) pick an
 * unmatched bill / invoice / receipt from the books and propose it as a
 * match for the bank line. When the amounts differ, the SME can capture
 * an optional adjustment (processing fee, FX, discount, write-off, etc.)
 * explaining the variance.
 *
 * Created matches land as SUGGESTED for CLIENT — reviewer still has to
 * approve. The chat card refreshes its "needs help" row to "Match proposed"
 * via optimistic UI update.
 */
export function MatchToExistingDialog({ target, onClose, onProposed }: Props) {
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>('PROCESSING_FEE');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentDescription, setAdjustmentDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // note: matches sibling AttachReceiptDialog pattern (custom backdrop, not Radix Dialog).
  // ESC + scroll-lock added for dialog-grade keyboard / focus behavior.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const debouncedQ = useDebounce(q.trim(), 250);
  const { data, isLoading } = useMatchTargets({ q: debouncedQ, limit: 50 });
  const propose = useProposeMatch();

  const bankAmountForScore = parseFloat(target.bankTransaction.amount);

  const candidates = useMemo(() => {
    type Candidate = {
      key: string;
      kind: 'entity' | 'document';
      id: string;
      matchedObjectType: MatchedObjectType;
      displayName: string;
      externalRef: string | null;
      amount: string | null;
      date: string | null;
      status: string | null;
      matchPercent: number | null;
    };
    if (!data) return [] as Array<Candidate>;

    const scoreOf = (amt: string | null): number | null => {
      if (!amt) return null;
      const n = parseFloat(amt);
      if (Number.isNaN(n) || Number.isNaN(bankAmountForScore)) return null;
      const base = Math.max(Math.abs(bankAmountForScore), Math.abs(n));
      if (base === 0) return 100;
      const diffRatio = Math.abs(bankAmountForScore - n) / base;
      return Math.max(0, Math.round(100 * (1 - diffRatio)));
    };

    const merged: Candidate[] = [
      ...data.entities.map((e) => ({ ...e, key: `e-${e.id}`, matchPercent: scoreOf(e.amount) })),
      ...data.documents.map((d) => ({ ...d, key: `d-${d.id}`, matchPercent: scoreOf(d.amount) })),
    ];

    merged.sort((a, b) => {
      const ap = a.matchPercent ?? -1;
      const bp = b.matchPercent ?? -1;
      return bp - ap;
    });

    return merged;
  }, [data, bankAmountForScore]);

  const selected = candidates.find(
    (c) => (c.kind === 'entity' ? `e-${c.id}` : `d-${c.id}`) === selectedId,
  );

  // Compute variance suggestion: bank line amount vs selected candidate amount.
  const bankAmount = bankAmountForScore;
  const candidateAmount = selected?.amount ? parseFloat(selected.amount) : null;
  const variance =
    candidateAmount != null && !Number.isNaN(bankAmount) && !Number.isNaN(candidateAmount)
      ? +(bankAmount - candidateAmount).toFixed(2)
      : null;
  const varianceAbs = variance != null ? Math.abs(variance) : null;
  const needsAdjustment = varianceAbs != null && varianceAbs >= 0.01;

  const submit = async () => {
    if (!selected) {
      toast.error('Pick a candidate first');
      return;
    }
    setSubmitting(true);
    try {
      await propose.mutateAsync({
        transactionId: target.transactionId,
        matchedObjectType: selected.matchedObjectType,
        matchedObjectId: selected.id,
        ...(adjustmentOpen && adjustmentAmount.trim()
          ? {
              adjustment: {
                type: adjustmentType,
                amount: adjustmentAmount.trim(),
                description: adjustmentDescription.trim() || undefined,
              },
            }
          : {}),
      });
      onProposed?.({
        matchedSummary: selected.displayName,
        matchedAmount: selected.amount ?? null,
        variance,
      });
      toast.success(`Match proposed: ${selected.displayName}. Eriko will confirm on review.`);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not propose match');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface-0 border border-surface-200 rounded-lg shadow-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-surface-900 mb-1">Match to an existing record</h3>
        <p className="text-xs text-surface-500 mb-3">
          For: <span className="font-mono">{target.bankTransaction.description}</span> ·{' '}
          <span className="font-mono">{formatCurrency(bankAmount, 2, target.currency)}</span> ·{' '}
          {formatDate(target.bankTransaction.date)}
        </p>

        {/* Search */}
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search bills, invoices, or receipts in your books…"
          className="mb-3 text-sm"
        />

        {/* Candidate list */}
        <section className="mb-4 border border-surface-200 rounded-md max-h-[300px] overflow-auto">
          {isLoading ? (
            <p className="text-xs text-surface-500 italic px-3 py-4">Loading candidates…</p>
          ) : candidates.length === 0 ? (
            <div className="px-3 py-4">
              <p className="text-xs text-surface-500 italic">
                {q ? 'No candidates match your search.' : 'No unmatched bills, invoices, or receipts in your books.'}
              </p>
              {!q && (
                <p className="text-[11px] text-surface-500 mt-1">
                  Records that are already paired with another bank line are
                  hidden here. Upload a new receipt below, or ask Eriko in chat
                  to break the existing match.
                </p>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-surface-100">
              {candidates.map((c) => {
                const key = c.kind === 'entity' ? `e-${c.id}` : `d-${c.id}`;
                const checked = key === selectedId;
                return (
                  <li key={key}>
                    <label
                      className={`flex items-start gap-2.5 px-3 py-2.5 cursor-pointer transition-colors hover:bg-surface-50 ${
                        checked ? 'bg-surface-100' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="candidate"
                        checked={checked}
                        onChange={() => setSelectedId(key)}
                        className="mt-1 accent-surface-900"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-medium text-surface-900 truncate">
                            {c.displayName}
                          </span>
                          <Badge variant="outline" className="text-[10px] font-normal">
                            {c.matchedObjectType}
                          </Badge>
                          {c.matchPercent != null && (
                            <Badge
                              variant="outline"
                              className={`text-[10px] font-medium tabular-nums ${
                                c.matchPercent === 100
                                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                                  : c.matchPercent >= 90
                                    ? 'border-amber-300 bg-amber-50 text-amber-700'
                                    : 'border-surface-200 text-surface-500'
                              }`}
                            >
                              {c.matchPercent}% match
                            </Badge>
                          )}
                          {c.externalRef && (
                            <span className="text-[10px] text-surface-500 font-mono">
                              #{c.externalRef}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-surface-500 mt-0.5 font-mono tabular-nums">
                          {c.amount ? formatCurrency(parseFloat(c.amount), 2, target.currency) : '—'}
                          {c.date ? ` · ${formatDate(c.date)}` : ''}
                          {c.status ? ` · ${c.status}` : ''}
                        </div>
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Variance hint + adjustment toggle */}
        {selected && (
          <section className="mb-4 p-3 rounded-lg border border-surface-200 bg-surface-50">
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="text-surface-700">
                Bank: <span className="font-mono">{formatCurrency(bankAmount, 2, target.currency)}</span>
                {' → '}
                {selected.displayName}: <span className="font-mono">{candidateAmount != null ? formatCurrency(candidateAmount, 2, target.currency) : '—'}</span>
              </span>
              {variance != null && (
                <Badge
                  variant="outline"
                  className={`text-[10px] ${
                    varianceAbs! < 0.01
                      ? 'border-emerald-300 text-emerald-700'
                      : 'border-amber-300 text-amber-700'
                  }`}
                >
                  {varianceAbs! < 0.01
                    ? 'Zero variance'
                    : `Variance: ${variance > 0 ? '+' : ''}${formatCurrency(variance, 2, target.currency)}`}
                </Badge>
              )}
            </div>
            {needsAdjustment && !adjustmentOpen && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs mt-2"
                onClick={() => {
                  setAdjustmentOpen(true);
                  setAdjustmentAmount(String(varianceAbs!.toFixed(2)));
                }}
              >
                Explain the {formatCurrency(varianceAbs!, 2, target.currency)} difference
              </Button>
            )}
          </section>
        )}

        {/* Adjustment section (collapsible) */}
        {adjustmentOpen && (
          <section className="mb-4 p-3 rounded-lg border border-surface-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-surface-700 uppercase tracking-wide">
                Variance adjustment
              </span>
              <button
                type="button"
                onClick={() => setAdjustmentOpen(false)}
                className="text-[11px] text-surface-500 hover:text-surface-700"
              >
                Remove
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Select
                value={adjustmentType}
                onValueChange={(v) => setAdjustmentType(v as AdjustmentType)}
              >
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Adjustment type" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(ADJUSTMENT_TYPE_LABELS) as AdjustmentType[]).map((t) => (
                    <SelectItem key={t} value={t} className="text-xs">
                      {ADJUSTMENT_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={adjustmentAmount}
                onChange={(e) => setAdjustmentAmount(e.target.value)}
                placeholder="0.00"
                inputMode="decimal"
                className="text-xs"
              />
            </div>
            <Input
              value={adjustmentDescription}
              onChange={(e) => setAdjustmentDescription(e.target.value)}
              placeholder="Optional note (e.g. PayPal fee on $1280 payment)"
              className="text-xs"
            />
            <p className="text-[10px] text-surface-500 mt-2 italic">
              Recorded with the match for the reviewer. (QBO journal-entry sync is a pending follow-up.)
            </p>
          </section>
        )}

        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button size="sm" disabled={!selected || submitting} onClick={submit}>
            {submitting ? 'Proposing…' : 'Propose match'}
          </Button>
        </div>
      </div>
    </div>
  );
}
